package mapsearch

/**
 * Performs DBSCAN clustering on the given [items].
 *
 * @param items The list of items to be clustered.
 * @param distanceThreshold The maximum distance for items to be considered neighbors.
 * @param minClusterSize The minimum number of points needed to form a dense region (cluster).
 * @param distanceFunction A function that computes the distance between two items.
 *
 * @return A Pair of:
 *  - List of unclustered (noise) items.
 *  - List of clusters (each cluster is a distinct list of items).
 *
 * ## Algorithm (DBSCAN):
 * 1. Initially mark all points as unvisited.
 * 2. For each unvisited point, check the set of neighboring points within [distanceThreshold].
 * 3. If the number of neighbors (including the point itself) is below [minClusterSize], mark it as noise.
 * 4. Otherwise, start a new cluster and iteratively add neighbors that meet the density criteria.
 * 5. After processing all points, output the labeled clusters and noise points.
 */
fun <T> dbScanCluster(
    items: List<T>,
    distanceThreshold: Double,
    minClusterSize: Int = 2,
    distanceFunction: (T, T) -> Double
): Pair<List<T>, List<List<T>>> {
    // Special case: empty input
    if (items.isEmpty()) return emptyList<T>() to emptyList()

    // clusterAssignments[i] = -1 => unvisited
    // clusterAssignments[i] = 0  => noise
    // clusterAssignments[i] = k  => cluster ID (k > 0)
    val clusterAssignments = IntArray(items.size) { -1 }
    var currentClusterId = 0

    /**
     * Finds the neighboring indices of [index] within [distanceThreshold].
     */
    fun regionQuery(index: Int): List<Int> {
        val neighbors = mutableListOf<Int>()
        val pointA = items[index]
        for (j in items.indices) {
            if (j == index) continue
            val pointB = items[j]
            if (distanceFunction(pointA, pointB) <= distanceThreshold) {
                neighbors.add(j)
            }
        }
        return neighbors
    }

    /**
     * Expands the cluster starting from [startIndex], using [neighbors] as the initial set.
     */
    fun expandCluster(startIndex: Int, neighbors: List<Int>, clusterId: Int) {
        // Assign the start point to the cluster
        clusterAssignments[startIndex] = clusterId

        // We'll treat neighbors as a queue to explore
        val queue = neighbors.toMutableList()
        while (queue.isNotEmpty()) {
            val currentIndex = queue.removeLast() // or removeAt(queue.lastIndex)
            // If the point was marked as noise before, reassign it to the cluster
            if (clusterAssignments[currentIndex] == 0) {
                clusterAssignments[currentIndex] = clusterId
            }
            // If it's unvisited, add to cluster and check its neighbors
            if (clusterAssignments[currentIndex] == -1) {
                clusterAssignments[currentIndex] = clusterId
                val currentNeighbors = regionQuery(currentIndex)
                // If it's a core point, add its neighbors to the queue
                if (currentNeighbors.size + 1 >= minClusterSize) {
                    // Only add those neighbors not yet assigned to a cluster
                    queue += currentNeighbors.filter {
                        clusterAssignments[it] == -1 || clusterAssignments[it] == 0
                    }
                }
            }
        }
    }

    // Main DBSCAN loop
    for (i in items.indices) {
        if (clusterAssignments[i] != -1) continue // already visited
        val neighbors = regionQuery(i)
        if (neighbors.size + 1 < minClusterSize) {
            // Mark as noise
            clusterAssignments[i] = 0
        } else {
            // Found a core point; start a new cluster
            currentClusterId++
            expandCluster(i, neighbors, currentClusterId)
        }
    }

    // Gather results
    val noiseItems = mutableListOf<T>()
    val clustersMap = mutableMapOf<Int, MutableList<T>>()

    for (i in items.indices) {
        when (val cid = clusterAssignments[i]) {
            0 -> noiseItems.add(items[i]) // noise
            in 1..Int.MAX_VALUE -> {
                clustersMap.getOrPut(cid) { mutableListOf() }.add(items[i])
            }
        }
    }

    // Make each cluster distinct() to remove duplicates
    val clusters = clustersMap.values.map { it.distinct() }
    return noiseItems to clusters
}
